import {
  ConversationApiService,
  GetWidgetConfigRequest,
  GetWidgetConfigResponse,
  WidgetPosition,
} from '@vendasta/conversation';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom, map } from 'rxjs';
import { DefaultPosition, MultiLocationWebchatConfig, WebchatConfig } from '@galaxy/conversation/webchat-widget';
import { AnalyticsService } from './analytics/analytics.service';
import { SessionService } from './session.service';


function toMultiLocationModel(config: GetWidgetConfigResponse, widgetId: string): MultiLocationWebchatConfig {
  return {
    name: config.name,
    color: config.color || 'dimgrey',
    isEnabled: config.isEnabled,
    textColor: config.textColor || 'light',
    accentColor: config.accentColor,
    accentTextColor: config.accentTextColor,
    position: toPosition(config.position),
    welcomeMessage: config.welcomeMessage,
    assistantName: config.assistantName,
    assistantAvatarUrl: config.assistantAvatarUrl,
    customGreetingMessage: config.customGreetingMessage,
    enableGreetingMessage: config.enableGreetingMessage,
    showFooterContent: config.showFooterContent,
    poweredByName: config.poweredByName,
    poweredByUrl: config.poweredByWebsite,
    groupId: widgetId,
    widgetType: 'group',
  };
}

function toModel(config: GetWidgetConfigResponse): WebchatConfig {
  return {
    name: config.name,
    color: config.color || 'dimgrey',
    isEnabled: config.isEnabled,
    welcomeMessage: config.welcomeMessage,
    textColor: config.textColor || 'light',
    accentColor: config.accentColor,
    accentTextColor: config.accentTextColor,
    skipContactCapture: config.skipContactCapture,
    widgetTopSpace: config.widgetTopSpace,
    assistantName: config.assistantName,
    assistantAvatarUrl: config.assistantAvatarUrl,
    position: toPosition(config.position),
    showMobileCta: !config.hideMobileCta,
    customGreetingMessage: config.customGreetingMessage,
    enableGreetingMessage: config.enableGreetingMessage,
    showFooterContent: config.showFooterContent,
    poweredByName: config.poweredByName,
    poweredByUrl: config.poweredByWebsite,
    namespace: config.namespace,
  };
}


function toPosition(pos: WidgetPosition): 'right' | 'left' {
  switch (pos) {
    case WidgetPosition.WIDGET_POSITION_LEFT:
      return 'left';
    case WidgetPosition.WIDGET_POSITION_RIGHT:
      return 'right';
    default:
      return DefaultPosition;
  }
}

@Injectable({
  providedIn: 'root',
})
export class WebchatConfigService {
  private readonly conversationApiService = inject(ConversationApiService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly sessionService = inject(SessionService);
  readonly widgetId = signal<string | null>(null);
  readonly selectedNamespace = signal<string | null>(null);
  readonly config = signal<WebchatConfig | null>(null);
  readonly multiLocationConfig = signal<MultiLocationWebchatConfig | null>(null);
  readonly activeConfig = computed(() => this.config() ?? this.multiLocationConfig());
  readonly dataQueryParams = signal<string | null>(null);
  readonly snapshotId = signal<string | null>(null);
  readonly hostPageUrl = signal('');
  readonly hostPageHostname = signal('');

  constructor() {
    effect(() => {
      this.analyticsService.setNamespace(this.config()?.namespace ?? this.selectedNamespace());
    });
  }

  async loadWidget(widgetId: string): Promise<void> {
    const req = new GetWidgetConfigRequest({
      widgetId: widgetId,
      visitData: this.sessionService.getVisit(),
    });
    const config = await firstValueFrom(this.conversationApiService.getWidgetConfig(req).pipe(map(toModel)));
    this.widgetId.set(widgetId);
    this.config.set({ ...config });
  }

  async loadMultiLocationWidget(widgetId: string): Promise<void> {
    const req = new GetWidgetConfigRequest({ widgetId, visitData: this.sessionService.getVisit() });
    const config = await firstValueFrom(
      this.conversationApiService.getWidgetConfig(req).pipe(map((r) => toMultiLocationModel(r, widgetId))),
    );
    this.widgetId.set(widgetId);
    this.multiLocationConfig.set(config);
  }

  setDataQueryParams(data: string): void {
    this.dataQueryParams.set(data);
  }

  setSnapshotId(snapshotId: string): void {
    this.snapshotId.set(snapshotId);
  }
}
